import React from 'react';

const OrderList = ({ orders, onUpdateStatus, onViewDetails, isDarkMode }) => {
    const styles = getStyles(isDarkMode);

    const getStatusStyle = (status) => {
        let bg = '#dfe6e9';
        let color = '#636e72';

        if (status === 'RECEIVED') { bg = '#3498db33'; color = '#3498db'; }
        if (status === 'ACCEPTED') { bg = '#f1c40f33'; color = '#f1c40f'; }
        if (status === 'PREPARING') { bg = '#e67e2233'; color = '#e67e22'; }
        if (status === 'READY') { bg = '#2ecc7133'; color = '#2ecc71'; }
        if (status === 'DISPATCHED') { bg = '#9b59b633'; color = '#9b59b6'; }
        if (status === 'COMPLETED') { bg = '#2ecc7133'; color = '#2ecc71'; }
        if (status === 'REJECTED' || status === 'CANCELLED') { bg = '#e74c3c33'; color = '#e74c3c'; }

        return {
            backgroundColor: bg,
            color: color,
            padding: '4px 10px',
            borderRadius: '20px',
            fontSize: '0.75rem',
            fontWeight: '700',
            textTransform: 'uppercase'
        };
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    return (
        <div style={styles.container}>
            <table style={styles.table}>
                <thead>
                    <tr style={styles.headerRow}>
                        <th style={styles.th}>Order ID</th>
                        <th style={styles.th}>Time</th>
                        <th style={styles.th}>Customer / Address</th>
                        <th style={styles.th}>Type</th>
                        <th style={styles.th}>Items</th>
                        <th style={styles.th}>Total</th>
                        <th style={styles.th}>Pay</th>
                        <th style={styles.th}>Status</th>
                        <th style={styles.th}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {orders.length === 0 && (
                        <tr>
                            <td colSpan="9" style={{ ...styles.td, textAlign: 'center', padding: '40px', color: '#888' }}>
                                No orders found.
                            </td>
                        </tr>
                    )}
                    {orders.map(order => (
                        <tr key={order.id} style={styles.row}>
                            <td style={{ ...styles.td, fontWeight: '600' }}>{order.orderNumber}</td>
                            <td style={{ ...styles.td, fontSize: '0.9rem', color: isDarkMode ? '#aaa' : '#666' }}>
                                {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td style={styles.td}>
                                <div style={{ fontWeight: '500' }}>{order.customerName || 'Guest'}</div>
                                {order.deliveryAddress && (
                                    <div style={{ fontSize: '0.8rem', color: '#888', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        📍 {order.deliveryAddress}
                                    </div>
                                )}
                                {!order.deliveryAddress && order.customerPhone && <div style={{ fontSize: '0.8rem', color: '#888' }}>{order.customerPhone}</div>}
                            </td>
                            <td style={styles.td}>
                                <span style={{
                                    padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem',
                                    backgroundColor: isDarkMode ? '#333' : '#f0f0f0'
                                }}>{order.orderType}</span>
                            </td>
                            <td style={{ ...styles.td, maxWidth: '200px' }}>
                                <div style={styles.itemsTruncated}>
                                    {order.items.map(i => `${i.quantity}x ${i.menuItemName}`).join(', ')}
                                </div>
                            </td>
                            <td style={{ ...styles.td, fontWeight: '700' }}>{formatCurrency(order.totalAmount)}</td>
                            <td style={styles.td}>
                                <span style={{
                                    fontSize: '0.75rem', fontWeight: 'bold',
                                    color: order.paymentStatus === 'PAID' ? '#00b894' : '#e17055'
                                }}>
                                    {order.paymentStatus || 'PENDING'}
                                </span>
                            </td>
                            <td style={styles.td}>
                                <span style={getStatusStyle(order.status)}>{order.status}</span>
                            </td>
                            <td style={styles.td}>
                                <div style={styles.actions}>
                                    <button onClick={() => onViewDetails(order)} style={styles.viewBtn}>View</button>

                                    {order.status === 'RECEIVED' && (
                                        <>
                                            <button onClick={() => onUpdateStatus(order.id, 'ACCEPTED')} style={styles.acceptBtn}>✓</button>
                                            <button onClick={() => onUpdateStatus(order.id, 'REJECTED')} style={styles.rejectBtn}>✗</button>
                                        </>
                                    )}

                                    {order.status === 'ACCEPTED' && (
                                        <button onClick={() => onUpdateStatus(order.id, 'PREPARING')} style={styles.actionBtn}>Start Prep</button>
                                    )}

                                    {order.status === 'PREPARING' && (
                                        <button onClick={() => onUpdateStatus(order.id, 'READY')} style={styles.successBtn}>Ready</button>
                                    )}

                                    {order.status === 'READY' && (
                                        <button onClick={() => onUpdateStatus(order.id, 'DISPATCHED')} style={styles.dispatchBtn}>Dispatch</button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const getStyles = (isDark) => ({
    container: {
        backgroundColor: isDark ? '#1e1e1e' : '#fff',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: isDark ? '0 4px 6px rgba(0,0,0,0.3)' : '0 4px 6px rgba(0,0,0,0.1)',
        border: isDark ? '1px solid #333' : '1px solid #e0e0e0'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        color: isDark ? '#fff' : '#333',
    },
    headerRow: {
        backgroundColor: isDark ? '#2d3436' : '#f9f9f9',
        borderBottom: isDark ? '2px solid #444' : '2px solid #eee'
    },
    th: {
        padding: '16px',
        textAlign: 'left',
        fontWeight: '600',
        fontSize: '0.9rem',
        color: isDark ? '#ccc' : '#555'
    },
    row: {
        borderBottom: isDark ? '1px solid #333' : '1px solid #f0f0f0',
        transition: 'background-color 0.2s',
        ':hover': {
            backgroundColor: isDark ? '#252525' : '#f5f5f5' // Note: inline styles don't support pseudo-classes, consider handling via class or state if crucial
        }
    },
    td: {
        padding: '16px',
        verticalAlign: 'middle',
        fontSize: '0.95rem'
    },
    itemsTruncated: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '180px',
        color: isDark ? '#bbb' : '#666',
        fontSize: '0.85rem'
    },
    actions: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center'
    },
    viewBtn: {
        padding: '6px 12px',
        borderRadius: '4px',
        border: isDark ? '1px solid #555' : '1px solid #ccc',
        backgroundColor: 'transparent',
        color: isDark ? '#ccc' : '#555',
        cursor: 'pointer',
        fontSize: '0.8rem'
    },
    actionBtn: {
        padding: '6px 12px',
        borderRadius: '4px',
        border: 'none',
        backgroundColor: '#0984e3',
        color: 'white',
        cursor: 'pointer',
        fontSize: '0.8rem',
        fontWeight: '500'
    },
    acceptBtn: {
        padding: '6px 10px',
        borderRadius: '4px',
        border: 'none',
        backgroundColor: '#00b894',
        color: 'white',
        cursor: 'pointer'
    },
    rejectBtn: {
        padding: '6px 10px',
        borderRadius: '4px',
        border: 'none',
        backgroundColor: '#d63031',
        color: 'white',
        cursor: 'pointer'
    },
    successBtn: {
        padding: '6px 12px',
        borderRadius: '4px',
        border: 'none',
        backgroundColor: '#00b894',
        color: 'white',
        cursor: 'pointer',
        fontSize: '0.8rem'
    },
    dispatchBtn: {
        padding: '6px 12px',
        borderRadius: '4px',
        border: 'none',
        backgroundColor: '#6c5ce7',
        color: 'white',
        cursor: 'pointer',
        fontSize: '0.8rem'
    }
});

export default OrderList;
